import type {
  EnvironmentId,
  ModelSelection,
  ThreadId,
  RuntimeMode,
  ProviderInteractionMode,
} from "@cmmd-forge/contracts";
import { Link } from "@tanstack/react-router";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "../../lib/touchTarget";
import { useState } from "react";
import type { useProviderUsageRead } from "~/usage/useProviderUsage";
import { useAccountContinuation } from "./useAccountContinuation";
import {
  type UsageLimitRecoveryState,
  resolveUsageLimitRecovery,
  recoveryAccountCapacity,
  usageLimitRecoveryDescription,
  usageLimitRecoveryTitle,
  usageLimitRecoveryTriggerLabel,
} from "./usageLimitRecoveryAccounts";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";

export interface UsageLimitRecoveryProps {
  readonly environmentId: EnvironmentId;
  readonly projectId: number | null;
  readonly runtimeId?: string;
  readonly threadId: ThreadId;
  readonly modelSelection: ModelSelection;
  readonly runtimeMode?: RuntimeMode;
  readonly interactionMode?: ProviderInteractionMode;
  readonly onResumed?: () => void;
}

export function UsageLimitRecoveryDialog(
  props: UsageLimitRecoveryProps & {
    readonly usage: ReturnType<typeof useProviderUsageRead>;
    readonly effectiveAccountId: string | null;
  },
) {
  const [open, setOpen] = useState(true);
  const [chosenAccountId, setChosenAccountId] = useState<string | null>(null);
  const continuation = useAccountContinuation(props);
  const { usage, effectiveAccountId } = props;
  const provider = props.modelSelection.provider;
  const recovery = resolveUsageLimitRecovery({ provider, effectiveAccountId, ...usage });
  const alternatives = usage.accounts.filter(
    ({ account }) =>
      provider === "codex" &&
      account.provider === provider &&
      account.providerAccountId !== effectiveAccountId &&
      !account.needsReconnect,
  );
  const selectedAccountId =
    continuation.lockedAccountId ??
    chosenAccountId ??
    (recovery.kind === "available" ? recovery.account.providerAccountId : null);
  const selected = alternatives.find(
    ({ account }) => account.providerAccountId === selectedAccountId,
  );
  const capacity = selected ? recoveryAccountCapacity(selected.snapshot) : null;
  const canResume = selected !== undefined && capacity !== "exhausted" && !usage.isPending;
  const description = selectedRecoveryDescription(recovery, canResume ? selected : undefined);
  function resume() {
    if (canResume) void continuation.resume(selected.account.providerAccountId);
  }
  if (continuation.state === "resumed") return null;
  return (
    <>
      <div className="mx-auto w-full max-w-3xl px-4 pb-2">
        <Button
          className={`${RESPONSIVE_TEXT_BUTTON_CLASS} max-w-full whitespace-normal text-left`}
          variant="outline"
          onClick={() => setOpen(true)}
        >
          {usageLimitRecoveryTriggerLabel(recovery)}
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogPopup>
          <DialogHeader>
            <DialogTitle>{usageLimitRecoveryTitle(recovery)}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <RecoveryAccountChoices
            accounts={alternatives}
            selectedAccountId={selectedAccountId}
            disabled={continuation.lockedAccountId !== null || usage.isPending}
            onChoose={setChosenAccountId}
          />
          {usage.accountsResolved && recovery.kind !== "exhausted" && provider === "codex" ? (
            <p className="mx-6 mb-4 text-sm">
              <Link
                className="inline-flex min-h-touch items-center underline underline-offset-4 md:min-h-0"
                to="/settings/providers"
              >
                Manage accounts
              </Link>
            </p>
          ) : null}
          {continuation.error ? (
            <p role="alert" className="px-6 text-sm text-destructive-foreground">
              {continuation.error}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              className={RESPONSIVE_TEXT_BUTTON_CLASS}
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Not now
            </Button>
            {canResume ? (
              <ResumeAccountAction
                switching={continuation.state === "switching"}
                unknownUsage={capacity === "unknown"}
                onResume={resume}
              />
            ) : (
              <RecoveryAction recovery={recovery} onRetry={usage.retry} />
            )}
          </DialogFooter>
        </DialogPopup>
      </Dialog>
    </>
  );
}

function selectedRecoveryDescription(
  recovery: UsageLimitRecoveryState,
  selected: ReturnType<typeof useProviderUsageRead>["accounts"][number] | undefined,
) {
  if (!selected) return usageLimitRecoveryDescription(recovery);
  const capacity = recoveryAccountCapacity(selected.snapshot);
  if (capacity === "available") {
    return usageLimitRecoveryDescription({ kind: "available", account: selected.account });
  }
  return usageLimitRecoveryDescription({ kind: "unknown" });
}

function ResumeAccountAction(props: {
  readonly switching: boolean;
  readonly unknownUsage: boolean;
  readonly onResume: () => void;
}) {
  const label = props.unknownUsage ? "Switch and try" : "Switch and resume";
  return (
    <Button
      className={RESPONSIVE_TEXT_BUTTON_CLASS}
      disabled={props.switching}
      onClick={props.onResume}
    >
      {props.switching ? "Resuming…" : label}
    </Button>
  );
}

function RecoveryAccountChoices(props: {
  readonly accounts: ReturnType<typeof useProviderUsageRead>["accounts"];
  readonly selectedAccountId: string | null;
  readonly disabled: boolean;
  readonly onChoose: (accountId: string) => void;
}) {
  if (props.accounts.length === 0) return null;
  return (
    <fieldset className="mx-6 mb-4 grid min-w-0 gap-2" disabled={props.disabled}>
      <legend className="mb-2 text-sm font-medium">Continue with</legend>
      {props.accounts.map(({ account, snapshot }) => {
        const accountCapacity = recoveryAccountCapacity(snapshot);
        return (
          <label
            key={account.providerAccountId}
            className="flex min-h-touch min-w-0 items-center gap-3 rounded-md border border-border px-3 py-2 text-sm"
          >
            <input
              type="radio"
              name="recovery-account"
              value={account.providerAccountId}
              checked={props.selectedAccountId === account.providerAccountId}
              disabled={accountCapacity === "exhausted"}
              onChange={() => props.onChoose(account.providerAccountId)}
            />
            <span className="min-w-0 flex-1">
              <span className="block break-all">
                {account.accountDisplayName || account.accountEmail || "Connected account"}
              </span>
              <span className="block text-muted-foreground">
                {accountCapacity === "available"
                  ? "Usage available"
                  : accountCapacity === "exhausted"
                    ? "Usage limit reached"
                    : "Usage unavailable"}
              </span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

function RecoveryAction(props: {
  readonly recovery: UsageLimitRecoveryState;
  readonly onRetry: () => void;
}) {
  switch (props.recovery.kind) {
    case "available":
      return (
        <Button className={RESPONSIVE_TEXT_BUTTON_CLASS} disabled>
          Choose an account
        </Button>
      );
    case "loading":
      return null;
    case "error":
    case "unknown":
      return (
        <Button className={RESPONSIVE_TEXT_BUTTON_CLASS} onClick={props.onRetry}>
          Try again
        </Button>
      );
    case "unsupported":
    case "exhausted":
      return (
        <Button className={RESPONSIVE_TEXT_BUTTON_CLASS} render={<Link to="/settings/providers" />}>
          Connect a new account
        </Button>
      );
  }
}
